import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDate, formatDateTime, formatMoney } from '../ui/format';
import { Badge, ErrorState, Loading, PageHeader, Panel } from '../ui/Page';
import { MemberSelect } from '../ui/UserSelect';
import {
  bandTone,
  finishOnboarding,
  getAccount,
  getPlan,
  listTemplates,
  patchAccount,
  renewalTone,
  rescoreAccount,
  RENEWAL_STATUSES,
  startOnboarding,
  type AccountDetail,
  type Health,
  type RenewalStatus,
  type SignalScore,
} from './api';

/**
 * One account.
 *
 * The score is shown with its working, always. A screen that says "62" and nothing else is a screen
 * nobody believes twice — the second time somebody is told a healthy account churned, a number they
 * cannot take apart stops being read. Every signal here says what it measured and what it found,
 * including the ones that found nothing.
 */
export function AccountPage(): React.JSX.Element {
  const { accountId = '' } = useParams();

  const account = useQuery({
    queryKey: ['cs-account', accountId],
    queryFn: () => getAccount(accountId),
    enabled: accountId !== '',
  });

  if (account.isLoading) return <Loading />;
  if (account.error) {
    return <ErrorState error={account.error} onRetry={() => void account.refetch()} />;
  }
  if (!account.data) return <Loading />;

  return <Detail account={account.data} />;
}

function Detail({ account }: { account: AccountDetail }): React.JSX.Element {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['cs-account', account.id] });
    void client.invalidateQueries({ queryKey: ['cs-accounts'] });
    void client.invalidateQueries({ queryKey: ['cs-summary'] });
  };

  const change = useMutation({
    mutationFn: (payload: Parameters<typeof patchAccount>[1]) => patchAccount(account.id, payload),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const rescore = useMutation({
    mutationFn: () => rescoreAccount(account.id),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  return (
    <div className="view view--wide">
      <PageHeader
        title={account.companyName}
        description={
          account.healthComputedAt
            ? t('cs.scoredAt').replace('{when}', formatDateTime(account.healthComputedAt))
            : t('cs.neverScored')
        }
        actions={
          <div className="cell-inline">
            <Link
              className="button button--quiet button--compact"
              to={`/crm/registro/companies/${account.companyId}`}
            >
              {t('cs.openCompany')}
            </Link>

            <button
              type="button"
              className="button button--quiet button--compact"
              disabled={rescore.isPending}
              onClick={() => rescore.mutate()}
            >
              {rescore.isPending ? t('cs.scoring') : t('cs.rescore')}
            </button>
          </div>
        }
      />

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <div className="ticket">
        <div className="ticket__thread">
          <Panel title={t('cs.health')}>
            {account.health ? (
              <HealthPanel health={account.health} score={account.healthScore} />
            ) : (
              <p className="muted">{t('cs.notScoredYet')}</p>
            )}
          </Panel>

          {account.trend.length > 1 && (
            <Panel title={t('cs.trend')}>
              <Trend trend={account.trend} />
            </Panel>
          )}

          <Onboarding accountId={account.id} />
        </div>

        <aside className="ticket__side">
          <Panel title={t('cs.contract')}>
            <div className="stack__field">
              <span>{t('cs.csm')}</span>
              <MemberSelect
                value={account.csmId}
                disabled={change.isPending}
                placeholder={t('cs.noCsm')}
                onChange={(next) => change.mutate({ csmId: next })}
              />
            </div>

            <label className="stack__field">
              <span>{t('cs.renewalDate')}</span>
              <input
                type="date"
                className="input"
                value={account.renewalDate ?? ''}
                disabled={change.isPending}
                onChange={(event) => change.mutate({ renewalDate: event.target.value || null })}
              />
            </label>

            <RenewalControl account={account} onChange={change.mutate} busy={change.isPending} />

            <dl className="facts">
              <dt>{t('cs.mrr')}</dt>
              <dd>{account.mrrCents === null ? '—' : formatMoney(account.mrrCents)}</dd>

              <dt>{t('cs.since')}</dt>
              <dd>{formatDate(account.startedAt)}</dd>

              {account.daysToRenewal !== null && (
                <>
                  <dt>{t('cs.renewal')}</dt>
                  <dd>
                    <Badge tone={renewalTone(account)}>
                      {account.daysToRenewal < 0
                        ? t('cs.renewalLate').replace('{n}', String(-account.daysToRenewal))
                        : t('cs.renewalIn').replace('{n}', String(account.daysToRenewal))}
                    </Badge>
                  </dd>
                </>
              )}

              {account.churnReason && (
                <>
                  <dt>{t('cs.churnReason')}</dt>
                  <dd>{account.churnReason}</dd>
                </>
              )}
            </dl>
          </Panel>
        </aside>
      </div>
    </div>
  );
}

/**
 * Marking a renewal.
 *
 * `CHURNED` asks for a reason before it will save, because a churn with no reason teaches nobody
 * anything and the moment to capture one is while somebody still remembers. The server refuses it
 * too; this is the same rule said where it is useful rather than instead of it.
 */
function RenewalControl({
  account,
  onChange,
  busy,
}: {
  account: AccountDetail;
  onChange: (payload: { renewalStatus: RenewalStatus; churnReason?: string }) => void;
  busy: boolean;
}): React.JSX.Element {
  const [status, setStatus] = useState<RenewalStatus>(account.renewalStatus);
  const [reason, setReason] = useState('');

  const needsReason = status === 'CHURNED' && !account.churnReason;

  return (
    <div className="stack__field">
      <span>{t('cs.renewalStatus')}</span>

      <select
        className="select"
        value={status}
        disabled={busy}
        onChange={(event) => {
          const next = event.target.value as RenewalStatus;
          setStatus(next);

          if (next !== 'CHURNED' || account.churnReason) onChange({ renewalStatus: next });
        }}
      >
        {RENEWAL_STATUSES.map((value) => (
          <option key={value} value={value}>
            {t(`cs.renewal.${value}`)}
          </option>
        ))}
      </select>

      {needsReason && (
        <div className="stack__field">
          <textarea
            rows={2}
            value={reason}
            placeholder={t('cs.churnReasonPlaceholder')}
            maxLength={1000}
            onChange={(event) => setReason(event.target.value)}
          />
          <button
            type="button"
            className="button button--compact"
            disabled={reason.trim() === '' || busy}
            onClick={() => onChange({ renewalStatus: 'CHURNED', churnReason: reason.trim() })}
          >
            {t('cs.recordChurn')}
          </button>
        </div>
      )}
    </div>
  );
}

/** Every signal, what it measured and what it found — including the ones that found nothing. */
function HealthPanel({
  health,
  score,
}: {
  health: Health;
  score: number | null;
}): React.JSX.Element {
  return (
    <div className="health-panel">
      <div className="health-panel__score">
        <span className={`health-panel__number health-panel__number--${health.band.toLowerCase()}`}>
          {score ?? health.score}
        </span>
        <Badge tone={bandTone(health.band)}>{t(`cs.band.${health.band}`)}</Badge>
        <span className="muted small">
          {t('cs.coverage').replace('{n}', String(Math.round(health.coverage * 100)))}
        </span>
      </div>

      <ul className="signals">
        {health.signals.map((signal) => (
          <SignalRow key={signal.signal} signal={signal} />
        ))}
      </ul>
    </div>
  );
}

function SignalRow({ signal }: { signal: SignalScore }): React.JSX.Element {
  const facts = Object.entries(signal.detail).filter(([, value]) => value !== null);

  return (
    <li className="signal">
      <span className="signal__name">{t(`cs.signal.${signal.signal}`)}</span>

      <span className="signal__track">
        {signal.score !== null && (
          <span
            className={`signal__fill signal__fill--${bandForScore(signal.score)}`}
            style={{ width: `${signal.score}%` }}
          />
        )}
      </span>

      <span className="signal__value">
        {signal.score === null ? <span className="muted">{t('cs.noData')}</span> : signal.score}
      </span>

      <span className="signal__detail muted small">
        {facts.length === 0
          ? t('cs.nothingRecorded')
          : facts.map(([key, value]) => `${t(`cs.detail.${key}`)}: ${String(value)}`).join(' · ')}
      </span>
    </li>
  );
}

function bandForScore(score: number): string {
  if (score >= 75) return 'healthy';
  if (score >= 55) return 'watch';
  if (score >= 35) return 'at_risk';

  return 'critical';
}

/**
 * The trend, as a sparkline drawn from the daily snapshots.
 *
 * Inline SVG rather than a charting library: it is a polyline over at most six months of points, and
 * shipping 90 kB to draw it would be the wrong trade on a page that already loads well.
 */
function Trend({ trend }: { trend: { on: string; score: number }[] }): React.JSX.Element {
  const width = 600;
  const height = 80;

  const points = trend
    .map((point, index) => {
      const x = trend.length === 1 ? 0 : (index / (trend.length - 1)) * width;
      const y = height - (point.score / 100) * height;

      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  const first = trend[0]!;
  const last = trend.at(-1)!;
  const delta = last.score - first.score;

  return (
    <div className="trend">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="trend__chart"
        role="img"
        aria-label={t('cs.trendLabel').replace('{n}', String(delta))}
      >
        <polyline points={points} />
      </svg>

      <p className="muted small">
        {t(delta >= 0 ? 'cs.trendUp' : 'cs.trendDown')
          .replace('{n}', String(Math.abs(delta)))
          .replace('{from}', formatDate(first.on))}
      </p>
    </div>
  );
}

/** The onboarding plan, or the offer to start one. */
function Onboarding({ accountId }: { accountId: string }): React.JSX.Element {
  const client = useQueryClient();
  const [templateId, setTemplateId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const plan = useQuery({
    queryKey: ['cs-plan', accountId],
    queryFn: () => getPlan(accountId),
  });

  const templates = useQuery({
    queryKey: ['cs-templates'],
    queryFn: listTemplates,
    staleTime: 5 * 60 * 1000,
  });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['cs-plan', accountId] });
    void client.invalidateQueries({ queryKey: ['cs-account', accountId] });
  };

  const start = useMutation({
    mutationFn: () => startOnboarding(accountId, templateId),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const finish = useMutation({
    mutationFn: (status: 'COMPLETED' | 'ABANDONED') => finishOnboarding(accountId, status),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const running = plan.data?.plan ?? null;

  return (
    <Panel
      title={t('cs.onboarding')}
      actions={
        running && (
          <button
            type="button"
            className="button button--quiet button--compact"
            disabled={finish.isPending}
            onClick={() => finish.mutate('COMPLETED')}
          >
            {t('cs.finishOnboarding')}
          </button>
        )
      }
    >
      {error && (
        <p className="alert small" role="alert">
          {error}
        </p>
      )}

      {plan.isLoading ? (
        <Loading />
      ) : running ? (
        <>
          <p className="muted small">
            {t('cs.planProgress')
              .replace('{done}', String(running.done))
              .replace('{total}', String(running.total))}
            {running.overdue > 0 &&
              ` · ${t('cs.planOverdue').replace('{n}', String(running.overdue))}`}
          </p>

          <ol className="plan">
            {running.steps.map((step) => (
              <li key={step.id} className={`plan__step${step.doneAt ? ' plan__step--done' : ''}`}>
                <span className="plan__mark" aria-hidden="true">
                  {step.doneAt ? '✓' : '○'}
                </span>

                <span className="cell-stack">
                  {/* The task is where the work happens; the plan just points at it. */}
                  {step.taskId ? (
                    <Link to={`/trabalho/tarefas/${step.taskId}`}>{step.title}</Link>
                  ) : (
                    <span>{step.title}</span>
                  )}
                  {step.dueDate && <span className="muted small">{formatDate(step.dueDate)}</span>}
                </span>
              </li>
            ))}
          </ol>
        </>
      ) : (
        <div className="cell-inline">
          <select
            className="select"
            value={templateId}
            onChange={(event) => setTemplateId(event.target.value)}
          >
            <option value="">{t('cs.pickTemplate')}</option>
            {(templates.data?.templates ?? [])
              .filter((template) => template.active)
              .map((template) => (
                <option key={template.id} value={template.id}>
                  {`${template.name} (${template.stepCount})`}
                </option>
              ))}
          </select>

          <button
            type="button"
            className="button button--compact"
            disabled={templateId === '' || start.isPending}
            onClick={() => start.mutate()}
          >
            {t('cs.startOnboarding')}
          </button>
        </div>
      )}
    </Panel>
  );
}
